import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import FEATURES from "../../config/features";
import { AppContexts } from "../../providers";
import Loading from "../common/Loading";
import ReservationsService from "../../services/reservations.service";
import StorageService from "../../services/storage.service";
import TaskOccurrencesService from "../../services/taskOccurrences.service";
import WorkSchedulesService from "../../services/work_schedules.service";
import WorkOrderService from "../../services/workorder.service";
import { initials } from "../work_order/work_order_calendar/calendarPresentationUtils";
import { formatCurrency } from "./storage/data";
import {
  addDaysISO,
  buildOperationsDashboardModel,
  normalizeWorkOrderBatch,
  ymdLocal,
} from "./operationsDashboardUtils";
import { normalizeWorkScheduleRecipients } from "./teamRecipients";

const INVENTORY_DASHBOARD_ENABLED =
  FEATURES.STORAGE_LOCATIONS_ENABLED ||
  FEATURES.STORAGE_ITEMS_ENABLED ||
  FEATURES.MANAGE_STOCK_ENABLED ||
  FEATURES.STOCK_MOVEMENTS_ENABLED;

const defaultInventoryStats = {
  total_inventory_items: 0,
  total_inventory_value: 0,
  low_stock_items: 0,
  lost_items_this_month: 0,
  damaged_items_this_month: 0,
  transfers_this_month: 0,
};

const DATE_FILTERS = [
  { value: "today", label: "Today" },
  { value: "tomorrow", label: "Tomorrow" },
  { value: "week", label: "Next 7 Days" },
];

const CATEGORY_META = {
  cleaning: { label: "Cleaning", icon: "home", filter: "clean" },
  inspection: { label: "Inspections", icon: "search", filter: "inspection" },
  maintenance: { label: "Maintenance", icon: "wrench", filter: "maintenance" },
  other: { label: "Other Tasks", icon: "file-text", filter: "task" },
};

const READINESS_LABELS = {
  blocked: "Blocked",
  risk: "At risk",
  "on-track": "On track",
  ready: "Ready",
};

function getDateWindow(filter) {
  const today = ymdLocal();
  if (filter === "tomorrow") {
    const tomorrow = addDaysISO(today, 1);
    return { startDate: tomorrow, endDate: tomorrow };
  }
  if (filter === "week") {
    return { startDate: today, endDate: addDaysISO(today, 6) };
  }
  return { startDate: today, endDate: today };
}

function formatDateRange(startDate, endDate) {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const options = { month: "short", day: "numeric" };
  if (startDate === endDate) {
    return start.toLocaleDateString(undefined, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });
  }
  return `${start.toLocaleDateString(undefined, options)} – ${end.toLocaleDateString(
    undefined,
    { ...options, year: "numeric" },
  )}`;
}

function formatCount(value) {
  return Number(value || 0).toLocaleString();
}

function MetricCard({ label, value, detail, tone = "neutral", onClick }) {
  const content = (
    <>
      <span className="ops-overview-metric-label">{label}</span>
      <strong>{formatCount(value)}</strong>
      <span className="ops-overview-metric-detail">{detail}</span>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        className={`ops-overview-metric is-${tone}`}
        onClick={onClick}
      >
        {content}
        <span className="ops-overview-metric-arrow" aria-hidden="true">→</span>
      </button>
    );
  }

  return <div className={`ops-overview-metric is-${tone}`}>{content}</div>;
}

function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="ops-overview-section-heading">
      <div>
        <h2>{title}</h2>
        {subtitle && <p>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export default function OperationsDashboard() {
  const history = useHistory();
  const rentalsContext = useContext(AppContexts.RentalsContext);
  const {
    rentals = [],
    loading: rentalsLoading,
    get: getRentals,
  } = rentalsContext || {};
  const rentalsRequestedRef = useRef(false);
  const [selectedFilter, setSelectedFilter] = useState("today");
  const [occurrences, setOccurrences] = useState([]);
  const [propertyStatuses, setPropertyStatuses] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [inventoryStats, setInventoryStats] = useState(defaultInventoryStats);
  const [loading, setLoading] = useState(true);
  const [loadWarning, setLoadWarning] = useState("");
  const { startDate, endDate } = useMemo(
    () => getDateWindow(selectedFilter),
    [selectedFilter],
  );

  useEffect(() => {
    document.title = "Operations Overview - RueBaRue";
  }, []);

  useEffect(() => {
    if (
      !rentals.length &&
      !rentalsLoading &&
      !rentalsRequestedRef.current &&
      getRentals
    ) {
      rentalsRequestedRef.current = true;
      getRentals();
    }
  }, [getRentals, rentals.length, rentalsLoading]);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setLoadWarning("");
    const requests = [
      TaskOccurrencesService.list({ date_from: startDate, date_to: endDate }),
      ReservationsService.searchReservation({
        start_date: startDate,
        end_date: endDate,
        preset: "staying-between",
        filters: "standard,owners,blocks",
        categories: ["STA", "OWN", "BLK", ""],
        page: 0,
        per_page: -1,
      }),
      WorkOrderService.getWorkOrders(undefined, {
        page: 0,
        per: 1000,
        sort: "updated_at",
      }),
      WorkSchedulesService.listRecipientsWithSchedules({ page: 0, per_page: 500 }),
    ];
    if (INVENTORY_DASHBOARD_ENABLED) {
      requests.push(StorageService.inventoryHealth());
    }

    try {
      const [occurrenceResult, reservationResult, workOrderResult, teamResult, inventoryResult] =
        await Promise.allSettled(requests);
      const failed = [];

      if (occurrenceResult.status === "fulfilled") {
        setOccurrences(occurrenceResult.value?.data?.occurrences || []);
        setPropertyStatuses(
          occurrenceResult.value?.data?.property_statuses || [],
        );
      } else {
        setOccurrences([]);
        setPropertyStatuses([]);
        failed.push("tasks");
      }

      if (reservationResult.status === "fulfilled") {
        const data = reservationResult.value?.data;
        setReservations(
          data?.reservations || data?.data || (Array.isArray(data) ? data : []),
        );
      } else {
        setReservations([]);
        failed.push("bookings");
      }

      if (workOrderResult.status === "fulfilled") {
        setWorkOrders(normalizeWorkOrderBatch(workOrderResult.value?.data));
      } else {
        setWorkOrders([]);
        failed.push("task exceptions");
      }

      if (teamResult.status === "fulfilled") {
        setTeamMembers(normalizeWorkScheduleRecipients(teamResult.value?.data));
      } else {
        failed.push("team workload");
      }

      if (inventoryResult?.status === "fulfilled") {
        setInventoryStats({
          ...defaultInventoryStats,
          ...(inventoryResult.value?.data || {}),
        });
      } else if (inventoryResult?.status === "rejected") {
        failed.push("inventory");
      }

      if (failed.length) {
        setLoadWarning(`Some data could not be loaded: ${failed.join(", ")}.`);
      }
    } finally {
      setLoading(false);
    }
  }, [endDate, startDate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const dashboard = useMemo(
    () =>
      buildOperationsDashboardModel({
        occurrences,
        workOrders,
        reservations,
        rentals,
        teamMembers,
        propertyStatuses,
        startDate,
        endDate,
      }),
    [endDate, occurrences, propertyStatuses, rentals, reservations, startDate, teamMembers, workOrders],
  );

  const calendarUrl = useCallback(
    (filters = {}) => {
      const params = new URLSearchParams({
        date: startDate,
        range: selectedFilter === "week" ? "7-day" : "3-day",
        ...filters,
      });
      return `/operations/calendar?${params.toString()}`;
    },
    [selectedFilter, startDate],
  );

  const openCalendar = (filters) => history.push(calendarUrl(filters));
  const scheduledLabel =
    selectedFilter === "today"
      ? "Due today"
      : selectedFilter === "tomorrow"
        ? "Scheduled tomorrow"
        : "Scheduled tasks";
  const scheduledValue =
    selectedFilter === "today"
      ? dashboard.stats.dueToday
      : dashboard.stats.totalTasks;
  const activeTeam = dashboard.team.filter((member) => member.assigned > 0).slice(0, 7);
  const visibleProperties = dashboard.properties.slice(0, 8);

  return (
    <section id="dashboard-template" className="ops-overview">
      <div id="hms-page-title" className="ops-overview-page-title">
        <div>
          <h1 className="uk-heading-small uk-margin-remove">Operations Overview</h1>
          <p>See what needs attention across properties and teams.</p>
        </div>
        <div className="ops-overview-page-actions">
          <button
            type="button"
            className="uk-button hms-btn hms-white-btn"
            onClick={() => history.push("/operations/team-scheduler")}
          >
            Team Scheduler
          </button>
          <button
            type="button"
            className="uk-button hms-btn hms-red-btn"
            onClick={() => openCalendar()}
          >
            Task Calendar
          </button>
        </div>
      </div>

      <div id="hms-main-body" className="ops-overview-body">
        <div className="ops-overview-toolbar">
          <div className="rbr-pill-toggle" aria-label="Dashboard date range">
            {DATE_FILTERS.map((filter) => (
              <button
                key={filter.value}
                type="button"
                className={`rbr-pill-toggle-button${
                  selectedFilter === filter.value ? " is-active" : ""
                }`}
                aria-pressed={selectedFilter === filter.value}
                onClick={() => setSelectedFilter(filter.value)}
              >
                {filter.label}
              </button>
            ))}
          </div>
          <span className="ops-overview-date-label">
            {formatDateRange(startDate, endDate)}
          </span>
          <button
            type="button"
            className="ops-overview-refresh"
            onClick={loadDashboard}
            disabled={loading}
          >
            <span uk-icon="icon: refresh; ratio: 0.75" /> Refresh
          </button>
        </div>

        {loadWarning && (
          <div className="calendar-data-warning ops-overview-warning" role="alert">
            <span uk-icon="icon: warning; ratio: 0.8" />
            {loadWarning}
            <button type="button" onClick={loadDashboard}>Retry</button>
          </div>
        )}

        {loading && !occurrences.length && !reservations.length ? (
          <Loading />
        ) : (
          <>
            <div className={`ops-overview-content${loading ? " is-refreshing" : ""}`}>
              <div className="ops-overview-metrics" aria-label="Operations exceptions">
                <MetricCard
                  label="Unassigned"
                  value={dashboard.stats.unassigned}
                  detail={`${dashboard.stats.unscheduled} unscheduled`}
                  tone={dashboard.stats.unassigned ? "warning" : "success"}
                  onClick={() => openCalendar({ assignment: "unassigned" })}
                />
                <MetricCard
                  label="Overdue"
                  value={dashboard.stats.overdue}
                  detail="Open tasks past due"
                  tone={dashboard.stats.overdue ? "danger" : "success"}
                  onClick={() => history.push("/operations/tasks")}
                />
                <MetricCard
                  label={scheduledLabel}
                  value={scheduledValue}
                  detail={`${dashboard.stats.completionPercent}% completed`}
                  tone="neutral"
                  onClick={() => openCalendar()}
                />
                <MetricCard
                  label="Arrivals"
                  value={dashboard.stats.arrivals}
                  detail="Guest check-ins"
                  tone="guest"
                  onClick={() => openCalendar({ type: "guest" })}
                />
                <MetricCard
                  label="Departures"
                  value={dashboard.stats.departures}
                  detail="Turnovers to manage"
                  tone="neutral"
                  onClick={() => openCalendar({ type: "guest" })}
                />
                <MetricCard
                  label="Blocked"
                  value={dashboard.stats.blocked}
                  detail="Properties unavailable"
                  tone={dashboard.stats.blocked ? "warning" : "neutral"}
                  onClick={() => openCalendar({ type: "block" })}
                />
              </div>

              <div className="ops-overview-primary-grid">
                <div className="ops-overview-panel ops-overview-readiness">
                  <SectionHeader
                    title="Property readiness"
                    subtitle="Exception-first view of properties with activity in this period."
                    action={
                      <button type="button" onClick={() => openCalendar()}>
                        View calendar <span aria-hidden="true">→</span>
                      </button>
                    }
                  />
                  {visibleProperties.length ? (
                    <div className="ops-overview-property-list">
                      {visibleProperties.map((property) => (
                        <button
                          type="button"
                          className="ops-overview-property-row"
                          key={property.id}
                          onClick={() => openCalendar({ property: property.id })}
                        >
                          <span className={`ops-readiness-dot is-${property.state}`} />
                          <span className="ops-property-identity">
                            <strong>{property.name}</strong>
                            <small>{property.address || `${property.tasks} scheduled tasks`}</small>
                          </span>
                          <span className="ops-property-activity">
                            {property.arrivals > 0 && <small>{property.arrivals} arrival{property.arrivals === 1 ? "" : "s"}</small>}
                            {property.departures > 0 && <small>{property.departures} departure{property.departures === 1 ? "" : "s"}</small>}
                            {!property.arrivals && !property.departures && <small>{property.tasks} task{property.tasks === 1 ? "" : "s"}</small>}
                          </span>
                          <span className={`ops-readiness-badge is-${property.state}`}>
                            {property.label || READINESS_LABELS[property.state]}
                          </span>
                          <span className="ops-row-arrow" aria-hidden="true">›</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="ops-overview-empty">
                      <span uk-icon="icon: check; ratio: 1.1" />
                      <strong>No property activity in this period</strong>
                      <span>Choose another date range or open the calendar.</span>
                    </div>
                  )}
                  {dashboard.properties.length > visibleProperties.length && (
                    <button className="ops-overview-show-all" type="button" onClick={() => openCalendar()}>
                      View all {dashboard.properties.length} active properties
                    </button>
                  )}
                </div>

                <div className="ops-overview-panel ops-overview-team">
                  <SectionHeader
                    title="Team workload"
                    subtitle="Assigned work for the selected period."
                    action={
                      <button type="button" onClick={() => history.push("/operations/team-scheduler")}>
                        Open scheduler <span aria-hidden="true">→</span>
                      </button>
                    }
                  />
                  {activeTeam.length ? (
                    <div className="ops-overview-team-list">
                      {activeTeam.map((member) => (
                        <button
                          type="button"
                          className="ops-overview-team-row"
                          key={member.id}
                          onClick={() =>
                            history.push(
                              `/operations/team-scheduler?date=${startDate}&range=${
                                selectedFilter === "week" ? "7-day" : "3-day"
                              }&user=${member.id}`,
                            )
                          }
                        >
                          {member.avatar ? (
                            <img src={member.avatar} alt="" className="ops-team-avatar" />
                          ) : (
                            <span className="ops-team-avatar is-initials">
                              {initials(member.name)}
                            </span>
                          )}
                          <span className="ops-team-identity">
                            <strong>{member.name}</strong>
                            <small>{member.inProgress} in progress · {member.completed} completed</small>
                          </span>
                          <span className="ops-team-count">
                            <strong>{member.assigned}</strong>
                            <small>assigned</small>
                          </span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="ops-overview-empty is-compact">
                      <strong>No assigned work in this period</strong>
                      <span>Unassigned work is still visible in the alert cards above.</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="ops-overview-panel ops-overview-task-health">
                <SectionHeader
                  title="Task health"
                  subtitle="Completion and assignment by operational category."
                  action={
                    <button type="button" onClick={() => history.push("/operations/tasks")}>
                      View all tasks <span aria-hidden="true">→</span>
                    </button>
                  }
                />
                <div className="ops-overview-categories">
                  {dashboard.categories.map((category) => {
                    const meta = CATEGORY_META[category.type];
                    return (
                      <button
                        type="button"
                        className={`ops-category-card is-${category.type}`}
                        key={category.type}
                        onClick={() => openCalendar({ type: meta.filter })}
                      >
                        <span className="ops-category-title">
                          <span className="ops-category-icon" uk-icon={`icon: ${meta.icon}; ratio: 0.8`} />
                          {meta.label}
                        </span>
                        <span className="ops-category-value">
                          <strong>{category.completed}</strong>
                          <small>of {category.total} completed</small>
                        </span>
                        <span className="ops-category-progress" aria-label={`${category.percent}% completed`}>
                          <i style={{ width: `${category.percent}%` }} />
                        </span>
                        <span className="ops-category-meta">
                          <span>{category.inProgress} in progress</span>
                          <span>{category.unassigned} unassigned</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {INVENTORY_DASHBOARD_ENABLED && (
                <div className="ops-overview-panel ops-overview-inventory">
                  <SectionHeader
                    title="Inventory health"
                    subtitle="A compact view of stock exceptions and asset movement."
                    action={
                      <button type="button" onClick={() => history.push("/operations/storage")}>
                        Open inventory <span aria-hidden="true">→</span>
                      </button>
                    }
                  />
                  <div className="ops-inventory-grid">
                    <div><strong>{formatCount(inventoryStats.total_inventory_items)}</strong><span>Total items</span></div>
                    <div><strong>{formatCurrency(inventoryStats.total_inventory_value)}</strong><span>Inventory value</span></div>
                    <div className={inventoryStats.low_stock_items ? "has-warning" : ""}><strong>{formatCount(inventoryStats.low_stock_items)}</strong><span>Low stock</span></div>
                    <div><strong>{formatCount(inventoryStats.damaged_items_this_month)}</strong><span>Damaged this month</span></div>
                    <div><strong>{formatCount(inventoryStats.transfers_this_month)}</strong><span>Transfers this month</span></div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
